import { MdbaseConnectError, connectError } from "./errors.js";
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
export const DEFAULT_STARTUP_TIMEOUT_MS = 10_000;
export const DEFAULT_FILE_INDEX_TIMEOUT_MS = 10 * 60_000;
export const DEFAULT_UPLOAD_TIMEOUT_MS = 120_000;
export const DEFAULT_SYNC_TIMEOUT_MS = 60_000;
export function resolveConnectTimeouts(timeouts = {}) {
    return {
        requestMs: configuredTimeout(timeouts.requestMs, DEFAULT_REQUEST_TIMEOUT_MS, "requestMs"),
        watchStartMs: configuredTimeout(timeouts.watchStartMs, DEFAULT_STARTUP_TIMEOUT_MS, "watchStartMs"),
        fileIndexMs: configuredTimeout(timeouts.fileIndexMs, DEFAULT_FILE_INDEX_TIMEOUT_MS, "fileIndexMs"),
        uploadMs: configuredTimeout(timeouts.uploadMs, DEFAULT_UPLOAD_TIMEOUT_MS, "uploadMs"),
        syncMs: configuredTimeout(timeouts.syncMs, DEFAULT_SYNC_TIMEOUT_MS, "syncMs")
    };
}
export function requestOptionsWithinBudget(options, budget) {
    return {
        ...options,
        signal: budget.signal,
        ...(budget.deadline === null ? {} : { deadlineUnixMs: budget.deadline })
    };
}
export function createRequestBudget(options = {}, defaultTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, now = Date.now) {
    const configured = options.timeoutMs === undefined ? defaultTimeoutMs : options.timeoutMs;
    const timeoutMs = configured === null ? null : validTimeout(configured);
    const controller = new AbortController();
    const deadline = timeoutMs === null ? null : now() + timeoutMs;
    const abortFromCaller = () => controller.abort(options.signal?.reason);
    if (options.signal?.aborted)
        abortFromCaller();
    else
        options.signal?.addEventListener("abort", abortFromCaller, { once: true });
    const timer = timeoutMs === null
        ? undefined
        : setTimeout(() => controller.abort(connectError("timeout", "The operation exceeded its request deadline.", { operationOutcome: "not_sent" })), timeoutMs);
    let disposed = false;
    return {
        signal: controller.signal,
        deadline,
        remainingMs: () => deadline === null ? null : Math.max(0, deadline - now()),
        dispose: () => {
            if (disposed)
                return;
            disposed = true;
            if (timer !== undefined)
                clearTimeout(timer);
            options.signal?.removeEventListener("abort", abortFromCaller);
        }
    };
}
export async function withRequestBudget(options, defaultTimeoutMs, operation) {
    const budget = createRequestBudget(options, defaultTimeoutMs);
    let rejectOnAbort;
    const aborted = new Promise((_resolve, reject) => {
        rejectOnAbort = reject;
    });
    const abort = () => rejectOnAbort?.(requestAbortReason(budget.signal));
    budget.signal.addEventListener("abort", abort, { once: true });
    try {
        if (budget.signal.aborted)
            throw requestAbortReason(budget.signal);
        return await Promise.race([operation(budget), aborted]);
    }
    finally {
        budget.signal.removeEventListener("abort", abort);
        budget.dispose();
    }
}
/**
 * Budget an operation whose transport cooperates with AbortSignal and must run
 * its own bounded cleanup/recovery before the public promise settles.
 */
export async function withCooperativeRequestBudget(options, defaultTimeoutMs, operation) {
    const budget = createRequestBudget(options, defaultTimeoutMs);
    try {
        if (budget.signal.aborted)
            throw requestAbortReason(budget.signal);
        return await operation(budget);
    }
    finally {
        budget.dispose();
    }
}
export function requestAbortReason(signal) {
    if (signal.reason instanceof MdbaseConnectError)
        return signal.reason;
    return connectError("operation_cancelled", "The operation was cancelled before it changed the collection.", { operationOutcome: "not_sent", cause: signal.reason });
}
function validTimeout(value) {
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new TypeError("timeoutMs must be a positive safe integer or null.");
    }
    return value;
}
function configuredTimeout(value, fallback, name) {
    if (value === undefined)
        return fallback;
    if (value === null)
        return null;
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new TypeError(`${name} must be a positive safe integer or null.`);
    }
    return value;
}
